import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@/lib/auth';
import {
  useSsoConnections, useUpsertSso, useDeleteSso, ssoAbsoluteUrl,
  type SsoConnection, type SsoProtocol,
} from '@/lib/sso';

/** Admin SSO configuration — one SAML and/or one OIDC connection per workspace. */
export function SsoTab() {
  const { t } = useTranslation();
  const { data: user } = useCurrentUser();
  const role = user?.role;
  const canManage = role === 'owner' || role === 'admin';
  const { data: connections } = useSsoConnections();
  const [protocol, setProtocol] = useState<SsoProtocol>('saml');

  const saml = connections?.find((c) => c.protocol === 'saml');
  const oidc = connections?.find((c) => c.protocol === 'oidc');

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="rounded-2xl border border-ink-200 bg-white p-6">
        <h2 className="text-base font-semibold text-ink-900">{t('sso.title', { defaultValue: 'Single sign-on (SAML / OIDC)' })}</h2>
        <p className="mt-1 text-sm text-ink-600">
          {t('sso.intro', { defaultValue: 'Let employees sign in through your identity provider (Okta, Entra ID, Google Workspace, Auth0). Users are provisioned into this workspace on first login at the role you choose.' })}
        </p>
        {!canManage && <p className="mt-3 text-sm text-ink-500">{t('sso.adminOnly', { defaultValue: 'Only workspace owners and admins can configure SSO.' })}</p>}
      </div>

      {connections && connections.length > 0 && (
        <div className="space-y-3">
          {connections.map((c) => <ConnectionCard key={c.id} conn={c} canManage={canManage} />)}
        </div>
      )}

      {canManage && (
        <div className="rounded-2xl border border-ink-200 bg-white p-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm font-semibold text-ink-900">{t('sso.addUpdate', { defaultValue: 'Add / update a connection' })}</span>
            <div className="ms-auto flex gap-1">
              {(['saml', 'oidc'] as SsoProtocol[]).map((p) => (
                <button key={p} onClick={() => setProtocol(p)}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold ${protocol === p ? 'bg-accent-500 text-white' : 'bg-ink-100 text-ink-700'}`}>
                  {p.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          {protocol === 'saml' ? <SamlForm existing={saml} /> : <OidcForm existing={oidc} />}
        </div>
      )}
    </div>
  );
}

function ConnectionCard({ conn, canManage }: { conn: SsoConnection; canManage: boolean }) {
  const { t } = useTranslation();
  const del = useDeleteSso();
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-5">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-ink-900">{conn.protocol.toUpperCase()}</span>
        <span className={`text-[11px] uppercase tracking-wider font-semibold ${conn.enabled ? 'text-emerald-600' : 'text-ink-400'}`}>
          {conn.enabled ? t('sso.enabled', { defaultValue: 'Enabled' }) : t('sso.disabled', { defaultValue: 'Disabled' })}
        </span>
        {conn.emailDomain && <span className="text-xs text-ink-500">@{conn.emailDomain}</span>}
        {canManage && (
          <button onClick={() => del.mutate(conn.id)} className="ms-auto text-xs font-semibold text-rose-600 hover:underline">{t('common.delete', { defaultValue: 'Delete' })}</button>
        )}
      </div>
      <dl className="mt-3 grid grid-cols-1 gap-1 text-xs text-ink-600">
        <Row label={t('sso.loginUrl', { defaultValue: 'Login URL' })} value={ssoAbsoluteUrl(conn.loginPath)} copyable />
        {conn.metadataPath && <Row label={t('sso.spMetadata', { defaultValue: 'SP metadata' })} value={ssoAbsoluteUrl(conn.metadataPath)} copyable />}
        <Row label={t('sso.defaultRole', { defaultValue: 'Default role' })} value={conn.defaultRole} />
        {conn.protocol === 'saml' && <Row label={t('sso.idpCert', { defaultValue: 'IdP cert' })} value={conn.samlIdpCertConfigured ? t('sso.configured', { defaultValue: 'configured' }) : t('sso.missing', { defaultValue: 'missing' })} />}
        {conn.protocol === 'oidc' && <Row label={t('sso.clientSecret', { defaultValue: 'Client secret' })} value={conn.oidcClientSecretConfigured ? t('sso.configured', { defaultValue: 'configured' }) : t('sso.missing', { defaultValue: 'missing' })} />}
      </dl>
    </div>
  );
}

function Row({ label, value, copyable }: { label: string; value: string; copyable?: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2">
      <span className="w-28 shrink-0 text-ink-400">{label}</span>
      <span className="font-mono break-all text-ink-800">{value}</span>
      {copyable && <button onClick={() => navigator.clipboard?.writeText(value)} className="text-accent-700 hover:underline">{t('common.copy', { defaultValue: 'copy' })}</button>}
    </div>
  );
}

const input = 'w-full h-10 rounded-lg border border-ink-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/20 focus:border-accent-500';
const ROLES = ['member', 'employee', 'manager', 'admin'] as const;

function SamlForm({ existing }: { existing?: SsoConnection }) {
  const { t } = useTranslation();
  const up = useUpsertSso();
  const [entryPoint, setEntryPoint] = useState(existing?.samlEntryPoint ?? '');
  const [cert, setCert] = useState('');
  const [emailDomain, setEmailDomain] = useState(existing?.emailDomain ?? '');
  const [defaultRole, setDefaultRole] = useState<SsoConnection['defaultRole']>(existing?.defaultRole ?? 'member');
  const [enabled, setEnabled] = useState(existing?.enabled ?? false);
  const keepHint = existing?.samlIdpCertConfigured ? t('sso.leaveBlankKeep', { defaultValue: '(leave blank to keep)' }) : '';
  return (
    <div className="space-y-3">
      <Field label={t('sso.samlEntryPoint', { defaultValue: 'IdP SSO URL (entryPoint)' })}><input className={input} value={entryPoint} onChange={(e) => setEntryPoint(e.target.value)} placeholder="https://idp.example.com/sso/saml" /></Field>
      <Field label={`${t('sso.samlCert', { defaultValue: 'IdP X.509 cert' })} ${keepHint}`}>
        <textarea className={`${input} h-24 py-2`} value={cert} onChange={(e) => setCert(e.target.value)} placeholder="MIIC8DCC…" />
      </Field>
      <DomainRoleEnabled {...{ emailDomain, setEmailDomain, defaultRole, setDefaultRole, enabled, setEnabled }} />
      <SaveButton pending={up.isPending} onClick={() => up.mutate({
        protocol: 'saml', enabled, emailDomain: emailDomain || undefined, defaultRole,
        samlEntryPoint: entryPoint || undefined, samlIdpCert: cert || undefined,
      })} />
      {up.isError && <Err />}
    </div>
  );
}

function OidcForm({ existing }: { existing?: SsoConnection }) {
  const { t } = useTranslation();
  const up = useUpsertSso();
  const [issuerUrl, setIssuerUrl] = useState(existing?.oidcIssuerUrl ?? '');
  const [clientId, setClientId] = useState(existing?.oidcClientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [emailDomain, setEmailDomain] = useState(existing?.emailDomain ?? '');
  const [defaultRole, setDefaultRole] = useState<SsoConnection['defaultRole']>(existing?.defaultRole ?? 'member');
  const [enabled, setEnabled] = useState(existing?.enabled ?? false);
  const keepHint = existing?.oidcClientSecretConfigured ? t('sso.leaveBlankKeep', { defaultValue: '(leave blank to keep)' }) : '';
  return (
    <div className="space-y-3">
      <Field label={t('sso.oidcIssuer', { defaultValue: 'Issuer URL' })}><input className={input} value={issuerUrl} onChange={(e) => setIssuerUrl(e.target.value)} placeholder="https://example.okta.com" /></Field>
      <Field label={t('sso.oidcClientId', { defaultValue: 'Client ID' })}><input className={input} value={clientId} onChange={(e) => setClientId(e.target.value)} /></Field>
      <Field label={`${t('sso.clientSecret', { defaultValue: 'Client secret' })} ${keepHint}`}>
        <input className={input} type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} />
      </Field>
      <DomainRoleEnabled {...{ emailDomain, setEmailDomain, defaultRole, setDefaultRole, enabled, setEnabled }} />
      <SaveButton pending={up.isPending} onClick={() => up.mutate({
        protocol: 'oidc', enabled, emailDomain: emailDomain || undefined, defaultRole,
        oidcIssuerUrl: issuerUrl || undefined, oidcClientId: clientId || undefined, oidcClientSecret: clientSecret || undefined,
      })} />
      {up.isError && <Err />}
    </div>
  );
}

function DomainRoleEnabled(p: {
  emailDomain: string; setEmailDomain: (v: string) => void;
  defaultRole: SsoConnection['defaultRole']; setDefaultRole: (v: SsoConnection['defaultRole']) => void;
  enabled: boolean; setEnabled: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <Field label={t('sso.emailDomain', { defaultValue: 'Email domain' })}><input className={input} value={p.emailDomain} onChange={(e) => p.setEmailDomain(e.target.value)} placeholder="acme.com" /></Field>
      <Field label={t('sso.defaultRole', { defaultValue: 'Default role' })}>
        <select className={input} value={p.defaultRole} onChange={(e) => p.setDefaultRole(e.target.value as SsoConnection['defaultRole'])}>
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </Field>
      <Field label={t('sso.enabledLabel', { defaultValue: 'Enabled' })}>
        <label className="flex items-center gap-2 h-10 text-sm text-ink-700"><input type="checkbox" checked={p.enabled} onChange={(e) => p.setEnabled(e.target.checked)} /> {t('sso.active', { defaultValue: 'active' })}</label>
      </Field>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className="block text-xs font-medium text-ink-600 mb-1">{label}</label>{children}</div>;
}
function SaveButton({ pending, onClick }: { pending: boolean; onClick: () => void }) {
  const { t } = useTranslation();
  return <button onClick={onClick} disabled={pending} className="rounded-lg bg-accent-500 text-white px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50">{pending ? t('common.saving', { defaultValue: 'Saving…' }) : t('sso.saveConnection', { defaultValue: 'Save connection' })}</button>;
}
function Err() {
  const { t } = useTranslation();
  return <p className="text-[13px] text-rose-600">{t('sso.saveError', { defaultValue: 'Could not save — check the required fields (cert/secret are required when enabling).' })}</p>;
}
